import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState, PageContainer } from '@/shared/ui';

import { useCardPool } from '../../hooks';
import { formatDateTime, formatPoints } from '../../lib/format';
import { useLoyaltyPermissions } from '../../lib/useLoyaltyPermissions';
import { CardPoolStatusChip } from '../components/chips';
import { GeneratePoolDialog } from '../components/GeneratePoolDialog';
import { ImportPoolDialog } from '../components/ImportPoolDialog';
import { CodeText } from '../components/primitives';

/** Free-card pool inventory — list + import + generate (gated by `loyalty.view`). */
export function LoyaltyCardPoolPage() {
  const { t } = useTranslation('loyalty');
  const { canView, canManage } = useLoyaltyPermissions();
  const pool = useCardPool({ page: 0, size: 100 }, { enabled: canView });
  const [importOpen, setImportOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  if (!canView) {
    return (
      <PageContainer title={t('cardPool.title')}>
        <ErrorState
          title={t('cardPool.accessRestrictedTitle')}
          message={t('cardPool.accessRestrictedMessage')}
        />
      </PageContainer>
    );
  }

  const cards = pool.data?.content ?? [];
  const available = pool.data?.availableCount;

  return (
    <PageContainer
      title={t('cardPool.title')}
      subtitle={t('cardPool.subtitle')}
      actions={
        canManage ? (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              onClick={() => setImportOpen(true)}
            >
              {t('common:action.import')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AutoFixHighRoundedIcon />}
              onClick={() => setGenerateOpen(true)}
            >
              {t('cardPool.generateAction')}
            </Button>
          </Stack>
        ) : undefined
      }
    >
      {!canManage ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('cardPool.viewOnlyNotice')}
        </Alert>
      ) : null}

      {available != null ? (
        <Box sx={{ mb: 2 }}>
          <Chip
            color="success"
            variant="outlined"
            label={t('cardPool.availableCount', { count: formatPoints(available) })}
          />
        </Box>
      ) : null}

      {pool.isLoading ? (
        <LoadingState label={t('cardPool.loading')} />
      ) : pool.isError ? (
        <ErrorState
          title={t('cardPool.loadError.title')}
          message={pool.error?.message ?? t('common:error.unknown')}
          onRetry={() => void pool.refetch()}
        />
      ) : cards.length === 0 ? (
        <EmptyState
          title={t('cardPool.empty.title')}
          description={t('cardPool.empty.description')}
          action={
            canManage ? (
              <Button
                variant="contained"
                startIcon={<AutoFixHighRoundedIcon />}
                onClick={() => setGenerateOpen(true)}
              >
                {t('cardPool.generateAction')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('cardPool.table.number')}</TableCell>
                  <TableCell>{t('cardPool.table.status')}</TableCell>
                  <TableCell>{t('cardPool.table.issuedTo')}</TableCell>
                  <TableCell>{t('cardPool.table.when')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cards.map((card) => (
                  <TableRow key={card.id} hover>
                    <TableCell>
                      <CodeText>{card.cardNumber}</CodeText>
                    </TableCell>
                    <TableCell>
                      <CardPoolStatusChip status={card.status} />
                    </TableCell>
                    <TableCell>
                      {card.issuedToCustomerId ? <CodeText>{card.issuedToCustomerId}</CodeText> : '—'}
                    </TableCell>
                    <TableCell>{formatDateTime(card.issuedAt ?? card.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <ImportPoolDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={(m) => setToast(m)} />
      <GeneratePoolDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onDone={(m) => setToast(m)}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(null)}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast ?? ''}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
